<script setup lang="tsx">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { commonProps } from './props'
import ClearButton from './ClearButton.vue'
import Multiselect from 'vue-multiselect'
import type { Responsible, Response } from '@/models/api-generated'
import http from '@/services/http'
import type { Column } from '@/stores/column'
import { useI18n } from 'vue-i18n'
import { getResponsibleIcon } from '@/services/utils'

const i18n = useI18n()
const isLoading = ref(false)
const isOpen = ref(false)
const model = defineModel<Column>({ required: true })
const props = defineProps(commonProps)
const options = ref<Responsible[]>([])

const internalModel = computed<Responsible[]>({
	get() {
		return model.value.filter
			.map((item: any) => {
				return options.value.find((opt) => opt.id === item.id && opt.type === item.type)
			})
			.filter(Boolean) as Responsible[]
	},
	set(value: Responsible[]) {
		model.value.filter = value.map((x) => ({
			id: x.id,
			type: x.type,
		}))
	},
})

onBeforeUnmount(() => (model.value.filter = []))
onBeforeMount(() => search(''))

async function search(q: string) {
	isLoading.value = true

	const { data } = await http.get<Response<Responsible[]>>('api/responsibles?q=' + q)

	if (!q) {
		// @ts-ignore
		data.data?.push({
			id: 0,
			type: '',
			label: i18n.t('misc.nothing'),
		})
	}

	options.value = data.data ?? []
	isLoading.value = false
}
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.id"
		:disabled="props.disabled"
		:placeholder="model.label + '...'"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="internalModel"
		label="label"
		track-by="trackBy"
		:limit="isOpen ? 100 : 3"
		@search-change="search"
		@open="isOpen = true"
		@close="isOpen = false"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>

		<template #option="{ option }">
			<div><i :class="getResponsibleIcon(option)"></i>{{ option.label }}</div>
		</template>

		<template #tag="{ option, remove }">
			<span class="multiselect__tag" :class="{ 'bg-dark': option.type === 'roles' }">
				<span>{{ option.label }}</span>
				<i class="multiselect__tag-icon" @mousedown.prevent.stop="remove(option)"></i>
			</span>
		</template>
	</Multiselect>
</template>
